import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect } from "react";
import { useApiClient } from "../../api/ApiContext";
import { ApiError } from "../../api/errors";
import { geofenceQueryKeys, myGeofenceReminderSchema } from "../../api/geofenceSchemas";
import type { MyGeofenceReminder } from "../../api/geofenceSchemas";
import { useOffline } from "../../offline/OfflineContext";
import { usePlatform } from "../../platform/PlatformContext";
import type { GeofenceReminders, GeofenceStatus } from "../../platform/PlatformServices";

/**
 * Version of the consent text (`geofence.consent.*`). A new version asks the person again; until then
 * the reminder stays off (Art. 7 GDPR, § 25 TDDDG).
 */
export const geofenceConsentVersion = "2026-10-09";

/** The consent of a person on this device: version of the text and when it was given. */
export interface GeofenceConsent {
  /** Version of the consent text. */
  readonly version: string;
  /** Milliseconds since the epoch. */
  readonly givenAtMs: number;
}

/**
 * Key of the consent of a person on this device; never sent to the server (O-34).
 *
 * @param owner - Owner key.
 * @returns The setting key.
 */
export function geofenceConsentSettingKey(owner: string): string {
  const key = `geofenceConsent:${owner}`;
  return key;
}

/**
 * Key of the last answer of the server, kept for offline use.
 *
 * @param owner - Owner key.
 * @returns The setting key.
 */
export function geofenceAnswerSettingKey(owner: string): string {
  const key = `geofenceReminder:${owner}`;
  return key;
}

/**
 * Reads a stored consent; a missing, unreadable or older one counts as not given.
 *
 * @param text - The stored JSON.
 * @returns The consent, or `null`.
 */
export function parseConsent(text: string | null): GeofenceConsent | null {
  if (text === null || text === "") {
    return null;
  }

  try {
    const value: unknown = JSON.parse(text);
    const record =
      typeof value === "object" && value !== null ? (value as Record<string, unknown>) : {};
    const version = record["version"];
    const givenAtMs = record["givenAtMs"];
    const consent =
      version === geofenceConsentVersion && typeof givenAtMs === "number"
        ? { version, givenAtMs }
        : null;

    return consent;
  } catch {
    return null;
  }
}

/**
 * Reads a stored answer; an unreadable one counts as unknown.
 *
 * @param text - The stored JSON.
 * @returns The answer, or `null`.
 */
function parseAnswer(text: string | null): MyGeofenceReminder | null {
  if (text === null || text === "") {
    return null;
  }

  try {
    const parsed = myGeofenceReminderSchema.safeParse(JSON.parse(text));
    const answer = parsed.success ? parsed.data : null;

    return answer;
  } catch {
    return null;
  }
}

/** Result of {@link useGeofenceReminder}. */
export interface GeofenceReminderControl {
  /** The capability of the device; `null` in the browser, where the function is hidden. */
  readonly reminders: GeofenceReminders | null;
  /**
   * The answer the app follows: the server's, offline the last one kept on the device; `null` while
   * unknown.
   */
  readonly answer: MyGeofenceReminder | null;
  /**
   * Whether the server refused the function (an error answer, for example after a rollback): the
   * device then removes everything.
   */
  readonly refused: boolean;
  /** The consent of the person on this device, or `null`. */
  readonly consent: GeofenceConsent | null;
  /** Whether the stored consent was read. */
  readonly consentLoaded: boolean;
  /** What the device can do and allows; `null` while unknown. */
  readonly status: GeofenceStatus | null;
  /**
   * Records the consent after the person read the information, then asks for the permissions.
   */
  readonly enable: () => Promise<void>;
  /** Revokes the consent and removes every region, alarm and reminder from the device. */
  readonly disable: () => Promise<void>;
  /** Reads the status of the device again (after the settings of the system were opened). */
  readonly refreshStatus: () => Promise<void>;
}

/**
 * The geofence reminder of a person (spec 12.4, T-188, O-34, ADR-0167): the tenant's release and the
 * regions from `GET /me/geofence-reminder` (a plain read; the app sends nothing back), the consent
 * of the person kept only on this device, and the status of the device. Shared through the query
 * cache, so the settings and the synchronization see the same state.
 *
 * @param owner - Owner key of the signed-in person, or `null`.
 * @returns The control.
 */
export function useGeofenceReminder(owner: string | null): GeofenceReminderControl {
  const client = useApiClient();
  const offline = useOffline();
  const platform = usePlatform();
  const queryClient = useQueryClient();
  const reminders = platform.geofence ?? null;
  const capable = reminders !== null && owner !== null;

  const answer = useQuery({
    queryKey: geofenceQueryKeys.mine,
    queryFn: ({ signal }) =>
      client.get("/api/v1/me/geofence-reminder", myGeofenceReminderSchema, { signal }),
    enabled: capable,
    staleTime: 5 * 60_000,
  });
  const stored = useQuery({
    queryKey: ["geofence-stored", owner],
    queryFn: async () =>
      owner === null
        ? null
        : parseAnswer(await offline.store.getSetting(geofenceAnswerSettingKey(owner))),
    enabled: capable,
    staleTime: Infinity,
  });
  const consent = useQuery({
    queryKey: ["geofence-consent", owner],
    queryFn: async () =>
      owner === null
        ? null
        : parseConsent(await offline.store.getSetting(geofenceConsentSettingKey(owner))),
    enabled: capable,
    staleTime: Infinity,
  });
  const status = useQuery({
    queryKey: ["geofence-status"],
    queryFn: () => (reminders === null ? null : reminders.status()),
    enabled: capable,
    staleTime: Infinity,
  });

  useEffect(() => {
    if (owner !== null && answer.data !== undefined) {
      void offline.store.putSetting(geofenceAnswerSettingKey(owner), JSON.stringify(answer.data));
    }
  }, [offline.store, owner, answer.data]);

  const refetchAnswer = answer.refetch;
  const refetchStatus = status.refetch;

  // Back in the foreground: the status may have changed in the settings, the periods move on.
  useEffect(() => {
    if (!capable) {
      return;
    }

    const unsubscribe = platform.onResume(() => {
      void refetchStatus();
      void refetchAnswer();
    });

    return unsubscribe;
  }, [capable, platform, refetchAnswer, refetchStatus]);

  const refused = answer.isError && answer.error instanceof ApiError;
  const current = refused ? null : (answer.data ?? stored.data ?? null);

  const enable = useCallback(async (): Promise<void> => {
    if (owner === null || reminders === null) {
      return;
    }

    const given: GeofenceConsent = { version: geofenceConsentVersion, givenAtMs: offline.nowMs() };
    await offline.store.putSetting(geofenceConsentSettingKey(owner), JSON.stringify(given));
    queryClient.setQueryData(["geofence-consent", owner], given);
    const allowed = await reminders.requestPermissions();
    queryClient.setQueryData(["geofence-status"], allowed);
  }, [offline, owner, queryClient, reminders]);

  const disable = useCallback(async (): Promise<void> => {
    if (owner === null || reminders === null) {
      return;
    }

    await offline.store.putSetting(geofenceConsentSettingKey(owner), "");
    queryClient.setQueryData(["geofence-consent", owner], null);
    await reminders.clear().catch(() => undefined);
  }, [offline.store, owner, queryClient, reminders]);

  const refreshStatus = useCallback(async (): Promise<void> => {
    await refetchStatus();
  }, [refetchStatus]);

  const control: GeofenceReminderControl = {
    reminders: capable ? reminders : null,
    answer: current,
    refused,
    consent: consent.data ?? null,
    consentLoaded: consent.isSuccess,
    status: status.data ?? null,
    enable,
    disable,
    refreshStatus,
  };

  return control;
}
